import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/firebase/api-client";
import { Store } from "@/types";
import { Plus, Edit2, Trash2, Package, Store as StoreIcon, User, Mail, Phone, Filter, Tag, Building2, AlertTriangle, Key, ArrowLeft, Truck, Check } from "lucide-react";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { AlertModal } from "@/components/shared/AlertModal";
import { ChangePasswordModal } from "@/components/shared/ChangePasswordModal";
import { useModal } from "@/hooks/useModal";
import { useCategories } from "@/hooks/useCategories";
import Loading from "@/components/shared/Loading";
import { useAuth } from "@/lib/hooks/useAuth";
import { UserRole } from "@/lib/constants";

interface CategoryDetail {
  id: string;
  name: string;
}

interface Fournisseur {
  id: string;
  userId: string;
  storeId: string;
  categories: string[];
  categoryDetails?: CategoryDetail[]; // Noms résolus par l'API (prioritaire pour l'affichage)
  category?: string;
  businessName?: string | null;
  store: { id: string; name: string } | null;
  user: {
    id: string;
    username: string;
    name: string;
    email?: string;
    phone?: string;
    isActive: boolean;
  } | null;
  createdAt: any;
  updatedAt: any;
}

export default function FournisseursPage() {
  // Supprimer et changer un mot de passe restent a la direction, meme
  // quand la page est ouverte a un support (18/10/2026).
  const { user: moi } = useAuth();
  const estAdmin = moi?.role === UserRole.ADMIN;
  const [fournisseurs, setFournisseurs] = useState<Fournisseur[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [showForm, setShowForm] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [editingFournisseur, setEditingFournisseur] = useState<Fournisseur | null>(null);
  const { categories: allCategories, loading: categoriesLoading } = useCategories(false); // Charger toutes les catégories (actives + inactives) pour résoudre les noms
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    storeId: "",
    categories: [] as string[], // IDs de catégories
    businessName: "",
    email: "",
    phone: "",
  });

  // Modales
  const deleteModal = useModal();
  const successModal = useModal();
  const errorModal = useModal();
  const changePasswordModal = useModal();
  const [deleteFournisseur, setDeleteFournisseur] = useState<{ id: string; name: string } | null>(null);
  const [changePasswordUser, setChangePasswordUser] = useState<{ id: string; name: string } | null>(null);
  const [modalMessage, setModalMessage] = useState({ title: "", message: "" });
  const [changingPassword, setChangingPassword] = useState(false);

  // Charger les données
  useEffect(() => {
    loadData();
  }, [selectedStore, selectedCategory]);

  async function loadData() {
    try {
      setLoading(true);
      const filters: { storeId?: string; category?: string } = {};
      if (selectedStore) filters.storeId = selectedStore;
      if (selectedCategory) filters.category = selectedCategory;
      
      const [fournsData, storesData] = await Promise.all([
        api.fournisseurs.getAll(Object.keys(filters).length > 0 ? filters : undefined).catch(() => []),
        api.stores.getAll().catch(() => []),
      ]);
      setFournisseurs(fournsData || []);
      setStores(storesData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      setModalMessage({
        title: "Erreur",
        message: "Erreur lors du chargement des données",
      });
      errorModal.open();
    } finally {
      setLoading(false);
    }
  }

  // Réinitialiser le formulaire
  function resetForm() {
    setFormData({
      username: "",
      password: "",
      name: "",
      storeId: "",
      categories: [],
      businessName: "",
      email: "",
      phone: "",
    });
    setEditingFournisseur(null);
    setShowForm(false);
    setCategorySearch("");
  }

  // Ouvrir formulaire de création
  function handleCreate() {
    resetForm();
    setShowForm(true);
  }

  // Ouvrir formulaire d'édition
  function handleEdit(fourn: Fournisseur) {
    if (!fourn.user) return;
    // Support ancien format (category) et nouveau format (categories) - filtrer les IDs orphelins
    const rawCategories = fourn.categories || (fourn.category ? [fourn.category] : []);
    const categories = rawCategories.filter((id) => allCategories.some((c) => c.id === id));
    setFormData({
      username: fourn.user.username,
      password: "",
      name: fourn.user.name,
      storeId: fourn.storeId,
      categories,
      businessName: fourn.businessName || "",
      email: fourn.user.email || "",
      phone: fourn.user.phone || "",
    });
    setEditingFournisseur(fourn);
    setShowForm(true);
  }

  // Soumettre le formulaire
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingFournisseur) {
        // Modifier
        const updateData: any = {
          phone: formData.phone || null,
          businessName: formData.businessName || null,
          categories: formData.categories,
        };
        if (formData.name !== editingFournisseur.user?.name) {
          updateData.name = formData.name;
        }
        await api.fournisseurs.update(editingFournisseur.id, updateData);
        setModalMessage({
          title: "Succès",
          message: "Fournisseur modifié avec succès",
        });
        successModal.open();
      } else {
        // Créer
        if (!formData.password) {
          setModalMessage({
            title: "Erreur",
            message: "Le mot de passe est obligatoire",
          });
          errorModal.open();
          return;
        }
        // Nettoyer les champs vides (les envoyer comme null)
        const createData = {
          ...formData,
          categories: formData.categories, // S'assurer que c'est un tableau d'IDs
          email: formData.email?.trim() || null,
          phone: formData.phone?.trim() || null,
          businessName: formData.businessName?.trim() || null,
        };
        console.log("Creating fournisseur with data:", createData); // Debug
        await api.fournisseurs.create(createData);
        setModalMessage({
          title: "Succès",
          message: "Fournisseur créé avec succès",
        });
        successModal.open();
      }
      resetForm();
      loadData();
    } catch (error: any) {
      console.error("Error saving fournisseur:", error);
      setModalMessage({
        title: "Erreur",
        message: error.message || "Erreur lors de la sauvegarde",
      });
      errorModal.open();
    }
  }

  // Ouvrir modal de suppression
  function handleDeleteClick(fourn: Fournisseur) {
    setDeleteFournisseur({ id: fourn.userId || fourn.id, name: fourn.user?.name || "" });
    deleteModal.open();
  }

  // Confirmer suppression
  async function handleDeleteConfirm() {
    if (!deleteFournisseur) return;

    try {
      await api.fournisseurs.delete(deleteFournisseur.id);
      deleteModal.close();
      setModalMessage({
        title: "Succès",
        message: "Fournisseur supprimé avec succès",
      });
      successModal.open();
      loadData();
    } catch (error: any) {
      console.error("Error deleting fournisseur:", error);
      deleteModal.close();
      setModalMessage({
        title: "Erreur",
        message: error.message || "Erreur lors de la suppression",
      });
      errorModal.open();
    }
  }

  // Ouvrir modal changement de mot de passe
  function handleChangePasswordClick(fourn: Fournisseur) {
    setChangePasswordUser({ id: fourn.userId || fourn.id, name: fourn.user?.name || "" });
    changePasswordModal.open();
  }

  // Confirmer changement de mot de passe
  async function handleChangePasswordConfirm(newPassword: string) {
    if (!changePasswordUser) return;

    try {
      setChangingPassword(true);
      await api.auth.changePassword(changePasswordUser.id, newPassword);
      setModalMessage({
        title: "Succès",
        message: "Mot de passe modifié avec succès",
      });
      successModal.open();
    } catch (error: any) {
      console.error("Error changing password:", error);
      throw error; // La modale gérera l'erreur
    } finally {
      setChangingPassword(false);
    }
  }

  // Filtrer les fournisseurs affichés
  let displayedFournisseurs = fournisseurs;
  if (selectedStore && !selectedCategory) {
    displayedFournisseurs = fournisseurs.filter((f) => f.storeId === selectedStore);
  }

  // Toggle catégorie dans la sélection
  function toggleCategory(categoryId: string) {
    const currentCategories = formData.categories;
    if (currentCategories.includes(categoryId)) {
      setFormData({
        ...formData,
        categories: currentCategories.filter((c) => c !== categoryId),
      });
    } else {
      setFormData({
        ...formData,
        categories: [...currentCategories, categoryId],
      });
    }
  }

  // Helper pour obtenir le nom d'une catégorie (null si orphelin/supprimé)
  function getCategoryName(categoryId: string): string | null {
    const category = allCategories.find((c) => c.id === categoryId);
    return category?.name ?? null;
  }

  /** Liste { id, name } pour l’affichage des catégories (priorité aux noms renvoyés par l’API). */
  function getCategoryDisplayList(fourn: Fournisseur): { id: string; name: string }[] {
    if (fourn.categoryDetails && fourn.categoryDetails.length > 0) {
      return fourn.categoryDetails;
    }
    const ids = fourn.categories || (fourn.category ? [fourn.category] : []);
    return ids.map((id) => ({ id, name: getCategoryName(id) ?? id }));
  }

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <Loading />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 md:mb-6">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 text-sm font-medium transition-colors duration-200 border border-gray-200 hover:border-gray-300"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour aux Utilisateurs</span>
        </Link>
      </div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold">Gestion des Fournisseurs</h1>
        <button
          onClick={handleCreate}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 flex items-center justify-center gap-2 min-h-[44px] w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          <span>Nouveau Fournisseur</span>
        </button>
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow mb-4 md:mb-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filtrer par Store
            </label>
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="w-full px-3 py-2 border rounded min-h-[44px]"
            >
              <option value="">Tous les stores</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Filtrer par Catégorie
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border rounded min-h-[44px]"
            >
              <option value="">Toutes les catégories</option>
              {allCategories.filter((c) => c.isActive).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {(selectedStore || selectedCategory) && (
          <p className="text-sm text-gray-600">
            {displayedFournisseurs.length} fournisseur(s) trouvé(s)
          </p>
        )}
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-4 md:mb-6 overflow-hidden">
          {/* Header du formulaire */}
          <div className="bg-gradient-to-r from-green-50 to-green-100/50 px-5 md:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-600" />
              {editingFournisseur ? "Modifier le Fournisseur" : "Nouveau Fournisseur"}
            </h2>
          </div>
          
          {/* Contenu du formulaire */}
          <form onSubmit={handleSubmit} className="p-5 md:p-6 space-y-5">
            {/* Section Authentification */}
            <div className="space-y-4 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Authentification
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Nom d'utilisateur <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    disabled={!!editingFournisseur}
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed min-h-[44px]"
                    placeholder="ex: boucher_maarif"
                  />
                  {editingFournisseur && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      Le nom d'utilisateur ne peut pas être modifié
                    </p>
                  )}
                </div>

                {!editingFournisseur && (
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Mot de passe <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                      placeholder="Minimum 6 caractères"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Section Informations personnelles */}
            <div className="space-y-4 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Informations personnelles
              </h3>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Nom complet <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                  placeholder="Ex: Hassan Benali"
                />
              </div>
            </div>

            {/* Section Affectation */}
            <div className="space-y-4 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Affectation
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Store <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    disabled={!!editingFournisseur}
                    value={formData.storeId}
                    onChange={(e) => setFormData({ ...formData, storeId: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    <option value="">Sélectionner un store</option>
                    {stores
                      .filter((s) => s.isActive)
                      .map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))}
                  </select>
                  {editingFournisseur && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      Le store ne peut pas être modifié
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Catégories <span className="text-gray-400 font-normal">(optionnel à la création)</span>
                    <span className="text-gray-400 font-normal ml-2">
                      ({formData.categories.length} sélectionnée{formData.categories.length > 1 ? "s" : ""})
                    </span>
                  </label>
                  {categoriesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loading />
                    </div>
                  ) : allCategories.length === 0 ? (
                    <p className="text-sm text-red-600">
                      Aucune catégorie disponible. Créez-en une dans la page Catégories.
                    </p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Search + Actions */}
                      <div className="p-2 bg-gray-50 border-b border-gray-200 space-y-2">
                        <input
                          type="text"
                          placeholder="Rechercher une catégorie..."
                          value={categorySearch}
                          onChange={(e) => setCategorySearch(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, categories: allCategories.map(c => c.id) })}
                            className="flex-1 text-xs py-1.5 px-2 bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
                          >
                            Tout sélectionner
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, categories: [] })}
                            className="flex-1 text-xs py-1.5 px-2 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 transition-colors"
                          >
                            Tout désélectionner
                          </button>
                        </div>
                      </div>
                      {/* Scrollable list */}
                      <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                        {allCategories
                          .filter(cat => cat.name.toLowerCase().includes(categorySearch.toLowerCase()))
                          .map((category) => {
                            const isSelected = formData.categories.includes(category.id);
                            return (
                              <button
                                key={category.id}
                                type="button"
                                onClick={() => toggleCategory(category.id)}
                                className={`w-full px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-all text-left ${
                                  isSelected
                                    ? "bg-primary/10 text-primary border border-primary/30"
                                    : "bg-white text-gray-700 border border-transparent hover:bg-gray-50"
                                }`}
                              >
                                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? "bg-primary border-primary" : "border-gray-300"
                                }`}>
                                  {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <span className="truncate">{category.name}</span>
                              </button>
                            );
                          })}
                        {allCategories.filter(cat => cat.name.toLowerCase().includes(categorySearch.toLowerCase())).length === 0 && (
                          <p className="text-center text-gray-400 text-sm py-4">Aucune catégorie trouvée</p>
                        )}
                      </div>
                    </div>
                  )}
                  {formData.categories.length === 0 && (
                    <p className="mt-1.5 text-sm text-amber-700 flex items-start gap-1">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      Aucune catégorie : tu peux les attribuer plus tard en éditant le fournisseur. Tant qu’il n’y en a pas, il ne sera pas mappé aux paniers par catégorie.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section Informations commerciales */}
            <div className="space-y-4 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Informations commerciales
              </h3>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Nom commercial (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                  placeholder="Ex: Boucherie Hassan"
                />
              </div>
            </div>

            {/* Section Contact */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Contact (optionnel)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Email (optionnel)</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                    placeholder="ex: hassan@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Téléphone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                    placeholder="+212600000000"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 min-h-[44px] font-semibold transition-colors duration-200 shadow-sm hover:shadow-md"
                >
                  {editingFournisseur ? "Modifier le Fournisseur" : "Créer le Fournisseur"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 sm:flex-initial bg-white text-gray-700 px-6 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 min-h-[44px] font-medium transition-colors duration-200"
                >
                  Annuler
                </button>
              </div>
              {editingFournisseur && estAdmin && (
                <button
                  type="button"
                  onClick={() => handleChangePasswordClick(editingFournisseur)}
                  className="w-full flex items-center justify-center gap-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 text-sm py-2.5 border border-gray-300 rounded-lg min-h-[44px] font-medium transition-colors duration-200"
                >
                  <Key className="w-4 h-4" />
                  Changer le mot de passe
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Liste des fournisseurs - Mobile: Cards */}
      <div className="md:hidden space-y-4">
        {displayedFournisseurs.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Aucun fournisseur trouvé</p>
          </div>
        ) : (
          displayedFournisseurs.map((fourn) => (
            <div key={fourn.id} className="bg-white p-4 rounded-lg shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-1">{fourn.user?.name || "-"}</h3>
                  {fourn.businessName && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      <Building2 className="w-4 h-4 flex-shrink-0" />
                      <span>{fourn.businessName}</span>
                    </div>
                  )}
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs flex-shrink-0 ${
                    fourn.user?.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {fourn.user?.isActive ? "Actif" : "Inactif"}
                </span>
              </div>
              <div className="space-y-2 text-sm text-gray-600 mb-3">
                <div className="flex items-center gap-2">
                  <StoreIcon className="w-4 h-4 flex-shrink-0" />
                  <span>{fourn.store?.name || `Store ID: ${fourn.storeId}`}</span>
                </div>
                <div className="flex items-start gap-2">
                  <Tag className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div className="flex flex-wrap gap-1 flex-1">
                    {getCategoryDisplayList(fourn).map((cat) => {
                      const category = allCategories.find((c) => c.id === cat.id);
                      const isObsolete = cat.name === cat.id;
                      return (
                        <span
                          key={cat.id}
                          className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${isObsolete ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}
                          style={category?.color && !isObsolete ? { backgroundColor: `${category.color}20`, color: category.color } : {}}
                          title={isObsolete ? "Catégorie supprimée ou non trouvée - modifier le fournisseur pour mettre à jour" : undefined}
                        >
                          {category?.color && !isObsolete && (
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: category.color }} />
                          )}
                          {cat.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 flex-shrink-0" />
                  <span>{fourn.user?.username || "-"}</span>
                </div>
                {fourn.user?.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span>{fourn.user.email}</span>
                  </div>
                )}
                {fourn.user?.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{fourn.user.phone}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-3 border-t">
                <button
                  onClick={() => handleEdit(fourn)}
                  className="flex-1 flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 text-sm py-2 border border-blue-600 rounded min-h-[44px] font-medium"
                >
                  <Edit2 className="w-4 h-4" />
                  Modifier
                </button>
{estAdmin && (
                <button
                  onClick={() => handleDeleteClick(fourn)}
                  className="flex-1 flex items-center justify-center gap-2 text-red-600 hover:text-red-800 text-sm py-2 border border-red-600 rounded min-h-[44px] font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer
                </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Nom</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Nom commercial</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Store</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Catégorie</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Username</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {displayedFournisseurs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    Aucun fournisseur trouvé
                  </td>
                </tr>
              ) : (
                displayedFournisseurs.map((fourn) => (
                  <tr key={fourn.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{fourn.user?.name || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {fourn.businessName || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {fourn.store?.name || `Store ID: ${fourn.storeId}`}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {getCategoryDisplayList(fourn).map((cat) => {
                          const category = allCategories.find((c) => c.id === cat.id);
                          const isObsolete = cat.name === cat.id;
                          return (
                            <span
                              key={cat.id}
                              className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${isObsolete ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}
                              style={category?.color && !isObsolete ? { backgroundColor: `${category.color}20`, color: category.color } : {}}
                              title={isObsolete ? "Catégorie supprimée ou non trouvée - modifier pour mettre à jour" : undefined}
                            >
                              {category?.color && !isObsolete && (
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: category.color }} />
                              )}
                              {cat.name}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">{fourn.user?.username || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {fourn.user?.email || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          fourn.user?.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {fourn.user?.isActive ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(fourn)}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          <Edit2 className="w-4 h-4" />
                          Modifier
                        </button>
{estAdmin && (
                        <button
                          onClick={() => handleDeleteClick(fourn)}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          Supprimer
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modales */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le Fournisseur"
        message={`Êtes-vous sûr de vouloir supprimer le fournisseur "${deleteFournisseur?.name}" ? Cette action est irréversible.`}
        type="danger"
        confirmText="Supprimer"
      />

      <AlertModal
        isOpen={successModal.isOpen}
        onClose={successModal.close}
        title={modalMessage.title}
        message={modalMessage.message}
        type="success"
      />

      <AlertModal
        isOpen={errorModal.isOpen}
        onClose={errorModal.close}
        title={modalMessage.title}
        message={modalMessage.message}
        type="error"
      />

      <ChangePasswordModal
        isOpen={changePasswordModal.isOpen}
        onClose={changePasswordModal.close}
        onConfirm={handleChangePasswordConfirm}
        userName={changePasswordUser?.name || ""}
        loading={changingPassword}
      />
    </div>
  );
}
